import { useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Plus, Trash2, Eye, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { Button } from '@/components/ui/button';
import { CofreDrawer } from './CofreDrawer';
import { CofreRevealModal } from './CofreRevealModal';
import { CofreDeleteDialog } from './CofreDeleteDialog';
import { useCofre, useDeleteCredencial, type Credencial } from '@/lib/hooks/cofre';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'Nunca usado';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function portalLabel(portal: string): string {
  if (portal === 'SUZANO') return 'Portal Suzano-SP';
  return portal;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  workspaceId: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CofreCard({ workspaceId }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [revealTarget, setRevealTarget] = useState<Credencial | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Credencial | null>(null);

  const { data, isLoading } = useCofre(workspaceId);
  const deleteMut = useDeleteCredencial(workspaceId);

  const credenciais = data?.items ?? [];

  async function handleDelete(userPassword: string) {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync({ id: deleteTarget.id, userPassword });
      toast.success('Credencial removida do cofre.');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover credencial.');
    }
  }

  return (
    <>
      <Card variant="default" className="p-5">
        {/* Cabeçalho do card */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2 bg-mint-soft text-mint-deep shrink-0">
              <KeyRound className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <Eyebrow>Segurança</Eyebrow>
              <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
                Cofre de credenciais
              </h3>
              <p className="text-sm text-fg-muted mt-0.5">
                Senhas do portal cifradas com sua chave — zero-knowledge.
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setDrawerOpen(true)}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" strokeWidth={1.8} />
            Adicionar
          </Button>
        </div>

        {/* Lista */}
        {isLoading ? (
          <div className="space-y-2" aria-busy="true" aria-label="Carregando credenciais">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 rounded-2 bg-card-soft animate-pulse" />
            ))}
          </div>
        ) : credenciais.length === 0 ? (
          <div className="rounded-2 border border-dashed border-card-stroke bg-card-soft p-6 text-center">
            <KeyRound
              className="mx-auto h-8 w-8 text-fg-dim mb-2"
              strokeWidth={1.4}
              aria-hidden="true"
            />
            <p className="text-sm text-fg-muted">Nenhuma credencial guardada ainda.</p>
            <p className="text-xs text-fg-dim mt-1">
              Adicione as credenciais do portal para usar a automação.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => setDrawerOpen(true)}
            >
              Guardar primeira credencial
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-card-stroke" role="list" aria-label="Credenciais salvas">
            {credenciais.map((cred) => (
              <li
                key={cred.id}
                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <Pill tone="mint" className="shrink-0 mt-0.5">
                    {portalLabel(cred.portal)}
                  </Pill>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg truncate">{cred.usuario}</p>
                    <p className="text-xs text-fg-muted flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3 shrink-0" strokeWidth={1.7} aria-hidden="true" />
                      {formatDate(cred.lastUsedAt)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    aria-label={`Revelar senha de ${portalLabel(cred.portal)} (${cred.usuario})`}
                    className="grid h-8 w-8 place-items-center rounded-2 text-fg-dim hover:text-mint-deep hover:bg-mint-soft transition-colors"
                    onClick={() => setRevealTarget(cred)}
                  >
                    <Eye className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                  <button
                    type="button"
                    aria-label={`Remover credencial de ${portalLabel(cred.portal)} (${cred.usuario})`}
                    className="grid h-8 w-8 place-items-center rounded-2 text-fg-dim hover:text-warn hover:bg-warn-soft transition-colors"
                    onClick={() => setDeleteTarget(cred)}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <CofreDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      <CofreRevealModal
        workspaceId={workspaceId}
        credencial={revealTarget}
        open={!!revealTarget}
        onOpenChange={(v) => {
          if (!v) setRevealTarget(null);
        }}
      />

      <CofreDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
        credencialLabel={
          deleteTarget ? `${portalLabel(deleteTarget.portal)} · ${deleteTarget.usuario}` : ''
        }
        loading={deleteMut.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
